import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    List,
    ListItem,
    ListItemText,
    Divider,
    Container,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ref, push, onValue, remove } from "firebase/database";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";

export default function Tracker() {
    const [labData, setLabData] = useState({
        creatinine: "",
        // egfr: "",
        // potassium: "",
    });
    const [testDate, setTestDate] = useState(new Date());
    const [records, setRecords] = useState([]);
    const navigate = useNavigate();

    // 🔒 Protect this page (redirect if not logged in)
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (!user) navigate("/login");
        });
        return () => unsubscribe();
    }, [navigate]);

    // 🔁 Listen for lab record changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (!user) return;

            const userRef = ref(db, `labRecords/${user.uid}`);
            onValue(userRef, (snapshot) => {
                const data = snapshot.val();
                if (data) {
                    const loadedRecords = Object.entries(data).map(([key, value]) => ({
                        id: key,
                        ...value,
                    }));
                    setRecords(loadedRecords.reverse());
                } else {
                    setRecords([]);
                }
            });
        });

        return () => unsubscribe();
    }, []);

    // 🖊️ Input handler
    const handleChange = (e) => {
        setLabData({ ...labData, [e.target.name]: e.target.value });
    };

    // 💾 Save a new record
    const handleSubmit = (e) => {
        e.preventDefault();
        const { creatinine } = labData;
        if (!creatinine) {
            alert("Please fill all fields");
            return;
        }

        const user = auth.currentUser;
        if (!user) return;

        const uid = user.uid;
        const userRef = ref(db, `labRecords/${uid}`);
        push(userRef, {
            creatinine,
            // egfr,
            // potassium,
            date: testDate.toISOString(),
        });

        setLabData({ creatinine: "" });
    };

    // ❌ Delete a record
    const handleDelete = (id) => {
        const user = auth.currentUser;
        if (!user) return;

        if (window.confirm("Are you sure you want to delete this record?")) {
            const recordRef = ref(db, `labRecords/${user.uid}/${id}`);
            remove(recordRef)
                .then(() => console.log("Deleted:", id))
                .catch((error) => console.error("Delete failed:", error));
        }
    };

    return (
        <Box
            sx={{
                height: "100vh",
                display: "flex",
                alignItems: "center",
                backgroundColor: "white",
                //backgroundImage: "url('https://images.unsplash.com/photo-1649073586428-e288125d930a?q=80&w=1932&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')", // Replace with your image
                backgroundSize: "cover",
                backgroundPosition: "center",
                color: "white",
                textAlign: "center",
            }}
        >
            <Container maxWidth="sm" sx={{ py: 6 }}>
                <Typography variant="h4" fontWeight="bold" align="center" gutterBottom>
                    Track Your Lab Results
                </Typography>

                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{ mb: 4, display: "flex", flexDirection: "column", gap: 2 }}
                >
                    <TextField
                        label="Creatinine (mg/dL)"
                        variant="outlined"
                        type="number"
                        step="0.01"
                        name="creatinine"
                        value={labData.creatinine}
                        onChange={handleChange}
                        required
                    />

                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Test Date"
                            value={testDate}
                            onChange={(newValue) => setTestDate(newValue)}
                            renderInput={(params) => <TextField {...params} required />}
                        />
                    </LocalizationProvider>

                    {/* Uncomment for future fields:
        <TextField
          label="eGFR (mL/min/1.73m²)"
          variant="outlined"
          type="number"
          step="1"
          name="egfr"
          value={labData.egfr}
          onChange={handleChange}
          required
        />
        <TextField
          label="Potassium (mmol/L)"
          variant="outlined"
          type="number"
          step="0.01"
          name="potassium"
          value={labData.potassium}
          onChange={handleChange}
          required
        /> */}

                    <Button type="submit" variant="contained" size="large">
                        Save Result
                    </Button>
                </Box>

                <Typography variant="h6" gutterBottom>
                    Previous Entries
                </Typography>

                <Paper
                    variant="outlined"
                    sx={{ maxHeight: 300, overflowY: "auto", px: 1, py: 0.5 }}
                >
                    <List>
                        {records.length === 0 ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                align="center"
                                sx={{ py: 2 }}
                            >
                                No entries yet.
                            </Typography>
                        ) : (
                            records.map(({ id, creatinine, date }) => (
                                <React.Fragment key={id}>
                                    <ListItem
                                        secondaryAction={
                                            <Button
                                                variant="text"
                                                color="error"
                                                size="small"
                                                onClick={() => handleDelete(id)}
                                            >
                                                Delete
                                            </Button>
                                        }
                                    >
                                        <ListItemText
                                            primary={`Creatinine: ${creatinine}`}
                                            secondary={new Date(date).toLocaleString()}
                                        />
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))
                        )}
                    </List>
                </Paper>
            </Container>
        </Box>
    );
}